/**
 * Торгівля (ОРЕ: РДН · ВДР) — РЕДИЗАЙН (VITE_REDESIGN).
 *
 * Спільна в'юха для /trading (обидва ринки), /trading/dam і /trading/idm.
 * Прод без прапорця бачить незмінений OreeMarketView (legacy) — див. page.tsx.
 *
 * Догма кольорів на цій сторінці:
 *   РДН              → --series-3 (приглушений брендовий; той самий тон, що на /market/prices)
 *   ВДР              → --warn     (бурштин: єдиний контрастний у ОБОХ темах не-червоний,
 *                                  брендова рампа другого такого не має)
 *   УЕБ BASE         → --series-2 пунктиром — довідкова лінія, не власний показник
 *   Факт. вироблення → --ok       (генерація)
 *   Потенц. виторг   → --text-muted (гроші = нейтраль, як на /financial/result)
 *   Зміна доба-до-доби → ↑ --ok / ↓ --warn / → --text-dim. Червоного немає ніде:
 *   падіння ціни — не збій системи.
 *
 * Контроли (глибина історії · доба профілю) живуть у топбарі (useTradingUi),
 * а не в тілі — стандарт §6 дизайн-контракту.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { api } from '@/services/api';
import { useTheme } from '@/hooks/useTheme';
import { useTradingUi } from '@/hooks/useTradingUi';
import {
  Activity, CalendarClock, Clock, Coins, MapPin, Minus, TrendingDown, TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import {
  Line, LineChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ComposedChart,
} from 'recharts';

type Market = 'DAM' | 'IDM';

interface SummaryRow {
  market: Market;
  trade_date: string;
  price: string | number | null;
  volume: string | number | null;
  prev_price: string | number | null;
  avg7d: string | number | null;
  day_diff_pct: string | number | null;
}
interface DailyRow { trade_date: string; market: Market; price: number; volume: number; declared_sell: number | null; declared_buy: number | null; }
interface HourlyRow { hour: number; market: Market; price: number; volume: number; }
interface VsUeexRow { trade_date: string; market: Market; oree_price: number; ueex_price: number | null; diff: number | null; }
interface VsGenRow { trade_date: string; hour: number; mwh: number; dam_price: number | null; estimated_revenue_uah: number | null; }

const fmt = (v: any, d = 0) =>
  (v == null || isNaN(Number(v))) ? '—' : Number(v).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });

/**
 * Recharts пише кольори в SVG-атрибути, а вони не резолвлять var(--…) —
 * тому токени доводиться діставати через getComputedStyle. Перерахунок
 * прив'язаний до isDark, інакше при перемиканні теми серії лишились би старими.
 */
function useTradingColors() {
  const { isDark } = useTheme();
  return useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb;
    return {
      dam: v('--series-3', isDark ? '#C56467' : '#A6575B'),
      idm: v('--warn', isDark ? '#E0A24A' : '#C77A1B'),
      ueex: v('--series-2', isDark ? '#C9CCCE' : '#5E656D'),
      gen: v('--ok', isDark ? '#4CAF6A' : '#2E7D46'),
      rev: v('--text-muted', isDark ? '#C9CCCE' : '#5E656D'),
      grid: v('--line', isDark ? 'rgba(255,255,255,0.08)' : '#E4E5E7'),
      tick: v('--text-dim', isDark ? '#7E868E' : '#9AA0A6'),
      text: v('--text', isDark ? '#F2F2F2' : '#28323C'),
      surface: v('--surface-solid', isDark ? '#2F3A45' : '#FFFFFF'),
    };
  }, [isDark]);
}

/** Скляна плитка-картка з графіком. Один рівень: усередині — жодних вкладених контурів. */
function Card({ Icon, title, note, children }: {
  Icon: LucideIcon; title: string; note?: string; children: React.ReactNode;
}) {
  return (
    <div className="tile p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={15} strokeWidth={1.9} style={{ color: 'var(--text-muted)' }} />
        <h3 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>{title}</h3>
      </div>
      {children}
      {note && <div className="text-[10.5px] mt-2" style={{ color: 'var(--text-dim)' }}>{note}</div>}
    </div>
  );
}

/** Легенда під графіком — власна, щоб кольори й типографіка йшли з токенів. */
function ChartLegend({ items }: { items: { label: string; color: string; dashed?: boolean }[] }) {
  return (
    <div className="flex items-center justify-center gap-x-5 gap-y-1.5 flex-wrap pt-3">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5"
          style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          <span style={{
            width: 16, height: 0, display: 'inline-block',
            borderTop: `3px ${it.dashed ? 'dashed' : 'solid'} ${it.color}`,
          }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function KpiTile({ Icon, color, title, value, unit, children }: {
  Icon: LucideIcon; color: string; title: string; value: string; unit?: string; children?: React.ReactNode;
}) {
  return (
    <div className="tile p-4 flex flex-col" style={{ minHeight: 132 }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} strokeWidth={2} style={{ color }} />
        <span className="text-[12.5px] font-semibold truncate" style={{ color: 'var(--text)' }} title={title}>{title}</span>
      </div>
      <div className="flex items-baseline gap-1 mt-2">
        <span className="font-display text-[22px] font-normal leading-none" style={{ color: 'var(--text)' }}>{value}</span>
        {unit && <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{unit}</span>}
      </div>
      <div className="mt-auto pt-2">{children}</div>
    </div>
  );
}

/** Зміна доба-до-доби: зростання зелене, падіння бурштинове. Червоного немає — догма. */
function DayChange({ pct }: { pct: number | string | null }) {
  if (pct == null || isNaN(Number(pct))) return null;
  const p = Number(pct);
  const up = p > 0, down = p < 0;
  const color = up ? 'var(--ok)' : down ? 'var(--warn)' : 'var(--text-dim)';
  const Ico = up ? TrendingUp : down ? TrendingDown : Minus;
  return (
    <span className="inline-flex items-center gap-1" style={{ color }}>
      <Ico size={12} strokeWidth={2.2} />{fmt(Math.abs(p), 2)}%
    </span>
  );
}

export default function OreeMarketViewRd({ marketFilter = null }: { marketFilter?: Market | null }) {
  const { t } = useTranslation();
  const c = useTradingColors();
  const { days, hourlyDate } = useTradingUi();

  const [summary, setSummary] = useState<SummaryRow[]>([]);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [hourly, setHourly] = useState<HourlyRow[]>([]);
  const [vsUeex, setVsUeex] = useState<VsUeexRow[]>([]);
  const [vsGen, setVsGen] = useState<VsGenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const mQs = marketFilter ? `&market=${marketFilter}` : '';
    Promise.all([
      api.get<{ markets: SummaryRow[] }>('/trading/oree/summary'),
      api.get<{ rows: DailyRow[] }>(`/trading/oree/daily?days=${days}${mQs}`),
      api.get<{ rows: HourlyRow[] }>(`/trading/oree/hourly?date=${hourlyDate}`),
      api.get<{ rows: VsUeexRow[] }>(`/trading/oree/vs-ueex?days=${days}`),
      api.get<{ rows: VsGenRow[] }>(`/trading/oree/vs-generation?days=7`),
    ]).then(([s, d, h, u, g]) => {
      if (cancelled) return;
      setSummary(s.markets || []);
      setDaily(d.rows || []);
      setHourly(h.rows || []);
      setVsUeex(u.rows || []);
      setVsGen(g.rows || []);
    }).catch(() => {}).finally(() => {
      if (!cancelled) { setLoading(false); setLoaded(true); }
    });
    return () => { cancelled = true; };
  }, [days, hourlyDate, marketFilter]);

  const dailyChart = useMemo(() => {
    const m: Record<string, any> = {};
    daily.forEach((r) => {
      const k = String(r.trade_date).slice(0, 10);
      m[k] = m[k] || { date: k };
      if (r.market === 'DAM') { m[k].dam = r.price; m[k].dam_vol = r.volume; }
      if (r.market === 'IDM') { m[k].idm = r.price; m[k].idm_vol = r.volume; }
    });
    return Object.values(m).sort((a: any, b: any) => a.date.localeCompare(b.date));
  }, [daily]);

  const hourlyChart = useMemo(() => {
    const m: Record<number, any> = {};
    hourly.forEach((r) => {
      m[r.hour] = m[r.hour] || { hour: `${r.hour}:00` };
      if (!marketFilter || r.market === marketFilter) {
        if (r.market === 'DAM') { m[r.hour].dam = r.price; m[r.hour].dam_vol = r.volume; }
        if (r.market === 'IDM') { m[r.hour].idm = r.price; m[r.hour].idm_vol = r.volume; }
      }
    });
    return Object.values(m).sort((a: any, b: any) => parseInt(a.hour) - parseInt(b.hour));
  }, [hourly, marketFilter]);

  const vsUeexChart = useMemo(() => {
    const m: Record<string, any> = {};
    vsUeex.forEach((r) => {
      if (marketFilter && r.market !== marketFilter) return;
      const k = String(r.trade_date).slice(0, 10);
      m[k] = m[k] || { date: k };
      if (r.market === 'DAM') m[k].oree_dam = r.oree_price;
      if (r.market === 'IDM') m[k].oree_idm = r.oree_price;
      m[k].ueex_base = r.ueex_price;
    });
    return Object.values(m).sort((a: any, b: any) => a.date.localeCompare(b.date));
  }, [vsUeex, marketFilter]);

  const vsGenChart = useMemo(() => {
    const m: Record<string, any> = {};
    vsGen.forEach((r) => {
      const k = String(r.trade_date).slice(0, 10);
      m[k] = m[k] || { date: k, mwh: 0, rev: 0 };
      m[k].mwh += Number(r.mwh) || 0;
      m[k].rev += Number(r.estimated_revenue_uah) || 0;
    });
    return Object.values(m).sort((a: any, b: any) => a.date.localeCompare(b.date));
  }, [vsGen]);

  const totalRev7d = vsGenChart.reduce((s: number, r: any) => s + r.rev, 0);
  const totalMwh7d = vsGenChart.reduce((s: number, r: any) => s + r.mwh, 0);

  const visibleSummary = marketFilter ? summary.filter((s) => s.market === marketFilter) : summary;
  const showDam = !marketFilter || marketFilter === 'DAM';
  const showIdm = !marketFilter || marketFilter === 'IDM';

  const axis = { tick: { fill: c.tick, fontSize: 10 }, axisLine: false, tickLine: false };
  const tipStyle = { background: c.surface, border: `1px solid ${c.grid}`, borderRadius: 10, fontSize: 12, color: c.text };
  const tipLabel = { color: c.text, fontWeight: 700 };

  /** Індикатор лише на ПЕРШОМУ завантаженні — інакше блимає при зміні періоду/дати. */
  const firstLoad = loading && !loaded;
  const Placeholder = ({ h }: { h: number }) => (
    <div className="flex items-center justify-center text-xs" style={{ color: 'var(--text-muted)', height: h }}>
      {t('ak_tr_loading')}
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Показники */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {visibleSummary.map((s) => (
          <KpiTile key={s.market}
            Icon={s.market === 'DAM' ? Activity : Clock}
            color={s.market === 'DAM' ? 'var(--series-3)' : 'var(--warn)'}
            title={s.market === 'DAM' ? t('oree_dam_label') : t('oree_idm_label')}
            value={fmt(s.price, 0)} unit={t('oree_kpi_price_unit_note')}>
            <div className="flex flex-col gap-0.5 text-[11px]" style={{ color: 'var(--text-muted)' }}>
              <span>{t('oree_volume_label')}: <span style={{ color: 'var(--text)' }}>{fmt(s.volume, 0)}</span> {t('ak_tr_mwh')}</span>
              <span className="flex items-center gap-2">
                <span>{t('ak_tr_7d_avg')} <span style={{ color: 'var(--text)' }}>{fmt(s.avg7d, 0)}</span></span>
                <DayChange pct={s.day_diff_pct} />
              </span>
              <span style={{ color: 'var(--text-dim)' }}>{String(s.trade_date).slice(0, 10)}</span>
            </div>
          </KpiTile>
        ))}

        <KpiTile Icon={Coins} color="var(--text-muted)" title={t('oree_potential_revenue_7d')}
          value={fmt(totalRev7d, 0)} unit={t('ak_tr_uah')}>
          <div className="flex flex-col gap-0.5 text-[11px]" style={{ color: 'var(--text-muted)' }}>
            <span>{t('ak_tr_mwh_actual', { n: fmt(totalMwh7d, 1) })}</span>
            <span style={{ color: 'var(--text-dim)' }}>{t('oree_potential_revenue_note')}</span>
          </div>
        </KpiTile>

        <KpiTile Icon={MapPin} color="var(--text-muted)" title={t('oree_tr_zone_label')}
          value={t('oree_kpi_zone_value')}>
          <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{t('oree_kpi_zone_note')}</div>
        </KpiTile>
      </div>

      {/* Динаміка цін за період */}
      <Card Icon={Activity} title={`${t('oree_daily_price_title')} · ${t('ak_tr_days', { n: days })}`}>
        {firstLoad ? <Placeholder h={280} /> : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={dailyChart} margin={{ top: 8, right: 10, left: -6, bottom: 0 }}>
                <CartesianGrid stroke={c.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" {...axis} />
                <YAxis {...axis} width={64} />
                <Tooltip contentStyle={tipStyle} labelStyle={tipLabel} cursor={{ stroke: c.grid }} />
                {showDam && <Line type="monotone" dataKey="dam" name={t('ak_tr_dam')} stroke={c.dam} strokeWidth={2} dot={false} isAnimationActive={false} />}
                {showIdm && <Line type="monotone" dataKey="idm" name={t('ak_tr_idm')} stroke={c.idm} strokeWidth={2} dot={false} isAnimationActive={false} />}
              </LineChart>
            </ResponsiveContainer>
            <ChartLegend items={[
              ...(showDam ? [{ label: t('ak_tr_dam_uah_mwh'), color: c.dam }] : []),
              ...(showIdm ? [{ label: t('ak_tr_idm_uah_mwh'), color: c.idm }] : []),
            ]} />
          </>
        )}
      </Card>

      {/* Погодинний профіль */}
      <Card Icon={CalendarClock} title={`${t('oree_hourly_profile_title')} · ${hourlyDate}`}>
        {firstLoad ? <Placeholder h={300} /> : hourlyChart.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm" style={{ color: 'var(--text-muted)' }}>
            <Clock size={22} strokeWidth={1.7} style={{ color: 'var(--text-dim)' }} />
            {t('oree_hourly_empty')}
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={hourlyChart} margin={{ top: 8, right: 10, left: -6, bottom: 0 }}>
                <CartesianGrid stroke={c.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="hour" {...axis} interval={1} />
                <YAxis yAxisId="left" {...axis} width={64} />
                <YAxis yAxisId="right" orientation="right" {...axis} width={56} />
                <Tooltip contentStyle={tipStyle} labelStyle={tipLabel} cursor={{ fill: c.grid }} />
                {showDam && <Bar yAxisId="right" dataKey="dam_vol" name={t('ak_tr_dam_volume')} fill={c.dam} fillOpacity={0.22} isAnimationActive={false} />}
                {showDam && <Line yAxisId="left" type="monotone" dataKey="dam" name={t('ak_tr_dam_price')} stroke={c.dam} strokeWidth={2} dot={false} isAnimationActive={false} />}
                {showIdm && <Line yAxisId="left" type="monotone" dataKey="idm" name={t('ak_tr_idm_price')} stroke={c.idm} strokeWidth={2} dot={false} isAnimationActive={false} />}
              </ComposedChart>
            </ResponsiveContainer>
            <ChartLegend items={[
              ...(showDam ? [{ label: t('ak_tr_dam_price'), color: c.dam }, { label: t('ak_tr_dam_volume'), color: c.dam, dashed: true }] : []),
              ...(showIdm ? [{ label: t('ak_tr_idm_price'), color: c.idm }] : []),
            ]} />
          </>
        )}
      </Card>

      {/* ОРЕ проти УЕБ BASE */}
      <Card Icon={Activity} title={t('oree_vs_ueex_title')}>
        {firstLoad ? <Placeholder h={240} /> : (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={vsUeexChart} margin={{ top: 8, right: 10, left: -6, bottom: 0 }}>
                <CartesianGrid stroke={c.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" {...axis} />
                <YAxis {...axis} width={64} />
                <Tooltip contentStyle={tipStyle} labelStyle={tipLabel} cursor={{ stroke: c.grid }} />
                {showDam && <Line type="monotone" dataKey="oree_dam" name={t('ak_tr_oree_dam')} stroke={c.dam} strokeWidth={2} dot={false} isAnimationActive={false} />}
                {showIdm && <Line type="monotone" dataKey="oree_idm" name={t('ak_tr_oree_idm')} stroke={c.idm} strokeWidth={2} dot={false} isAnimationActive={false} />}
                <Line type="monotone" dataKey="ueex_base" name={t('ak_tr_ueex_base')} stroke={c.ueex} strokeWidth={2} dot={false} strokeDasharray="4 4" isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            <ChartLegend items={[
              ...(showDam ? [{ label: t('ak_tr_oree_dam'), color: c.dam }] : []),
              ...(showIdm ? [{ label: t('ak_tr_oree_idm'), color: c.idm }] : []),
              { label: t('ak_tr_ueex_base_realized'), color: c.ueex, dashed: true },
            ]} />
          </>
        )}
      </Card>

      {/* ОРЕ проти фактичного вироблення */}
      <Card Icon={TrendingUp} title={t('oree_vs_generation_title')} note={t('oree_vs_generation_note')}>
        {firstLoad ? <Placeholder h={240} /> : (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={vsGenChart} margin={{ top: 8, right: 10, left: -6, bottom: 0 }}>
                <CartesianGrid stroke={c.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" {...axis} />
                <YAxis yAxisId="left" {...axis} width={64} />
                <YAxis yAxisId="right" orientation="right" {...axis} width={64} />
                <Tooltip contentStyle={tipStyle} labelStyle={tipLabel} cursor={{ fill: c.grid }} />
                <Bar yAxisId="left" dataKey="mwh" name={t('ak_tr_actual_generation')} fill={c.gen} fillOpacity={0.45} stroke={c.gen} strokeWidth={1.2} isAnimationActive={false} />
                <Line yAxisId="right" type="monotone" dataKey="rev" name={t('ak_tr_potential_revenue')} stroke={c.rev} strokeWidth={2} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
            <ChartLegend items={[
              { label: t('ak_tr_actual_generation_mwh_2'), color: c.gen },
              { label: t('ak_tr_potential_revenue_uah'), color: c.rev },
            ]} />
          </>
        )}
      </Card>
    </div>
  );
}
